import React from "react"
import toast from "react-hot-toast"
import { useApp } from "../context/AppContext"
import { Icon, useT, useIsMobile } from "../shared"
import { useOrgName } from "../shared/BrandLogo"
import { Btn, Tabs, TableWrap, ExportMenuGroups } from "../shared/components"
import {
  programProgress,
  programCoursesProgress,
  programAttendance,
  downloadProgressCsv,
  downloadProgressPdf,
  downloadProgressXlsx,
  downloadCoursesProgressCsv,
  downloadCoursesProgressPdf,
  downloadCoursesProgressXlsx,
  downloadAttendanceCsv,
  downloadAttendanceDetailCsv,
  downloadAttendanceDetailXlsx,
  downloadAttendanceDetailPdf,
  downloadAttendanceMatrixCsv,
  downloadAttendanceMatrixXlsx,
  downloadAttendanceMatrixPdf,
  downloadAttendancePdf,
  downloadAttendanceXlsx,
  downloadClientReportPdf,
  downloadClientReportCsv,
  downloadClientReportXlsx,
  downloadTestAnswersXlsx,
  downloadTestAnswersCsv,
  downloadImoComplianceCsv,
  downloadImoCompliancePdf,
  downloadImoComplianceXlsx,
} from "../api/users.api"
import { listSchedules, createSchedule, toggleSchedule, deleteSchedule } from "../api/reports.schedules.api"
import ReportFilterBar from "./reports/ReportFilterBar"
import RingkasFilterBar from "./reports/RingkasFilterBar"
import {
  SARINGAN_KOSONG, SARINGAN_SESI_KOSONG, SARINGAN_COURSE_KOSONG, KUNCI_SARING, keQuery, tidakIkut,
  jumlahAktif, hitungHuruf, labelAktif, rentangSalah, bacaSaringan, simpanSaringan, lingkupSaringan,
} from "../lib/reportFilters"

// Tiap tab yang bisa disaring: bentuk saringan kosongnya, jenis laporannya di
// server, dan sumber barisnya.
const KOSONG_TAB = {
  participants: SARINGAN_KOSONG,
  compliance: SARINGAN_KOSONG,
  attendance: SARINGAN_SESI_KOSONG,
  courses: SARINGAN_COURSE_KOSONG,
}
const JENIS_TAB = { participants: "progress", compliance: "progress", attendance: "sesi", courses: "course" }

const URUTAN_SESI = [
  ["jadwal", "asc", "Jadwal (terlama)", "Schedule (oldest)"],
  ["jadwal", "desc", "Jadwal (terbaru)", "Schedule (newest)"],
  ["judul", "asc", "Judul (A–Z)", "Title (A–Z)"],
  ["judul", "desc", "Judul (Z–A)", "Title (Z–A)"],
  ["hadir", "desc", "Hadir terbanyak", "Most attended"],
  ["hadir", "asc", "Hadir tersedikit", "Least attended"],
  ["ditandai", "desc", "Paling banyak ditandai", "Most flagged"],
]
const URUTAN_COURSE = [
  ["", "", "Urutan course", "Course order"],
  ["judul", "asc", "Judul (A–Z)", "Title (A–Z)"],
  ["judul", "desc", "Judul (Z–A)", "Title (Z–A)"],
  ["selesai", "asc", "Penyelesaian terendah", "Lowest completion"],
  ["selesai", "desc", "Penyelesaian tertinggi", "Highest completion"],
]

/* ============================================================
   Reports — admin & trainer
   Lima laporan per program (progres peserta, progres course,
   kehadiran sesi, laporan klien, kepatuhan IMO) plus jadwal
   pengiriman otomatis. Tiap tab menampilkan ringkasan + data di
   layar; SEMUA unduhan (CSV/Excel/PDF) ada di satu menu "Unduh
   laporan" di header, di tempat yang sama untuk setiap tab.
   ============================================================ */

// Dipakai hanya bila backend lama belum mengirim ambang per baris.
const AMBANG_BAWAAN = { attendance: 80, posttest: 70 }

const rata = (daftar) => {
  const v = daftar.filter((x) => x != null && !Number.isNaN(x))
  return v.length ? v.reduce((a, b) => a + b, 0) / v.length : null
}
const bulat = (n) => (n == null ? "—" : String(Math.round(n)))

// Kelompokkan `rows` per kelas, terurut sesuai tanggal mulai kelasnya —
// urutan yang sama dipakai backend saat mengelompokkan CSV/Excel/PDF (lihat
// reportGrouping.service.js), supaya tampilan di layar dan berkas yang
// diunduh dari tab yang sama tidak pernah menyebut kelas dalam urutan
// berbeda. Baris tanpa kelas (classId null/legacy) jatuh ke kelompok
// tersendiri, selalu di akhir — bukan hilang begitu saja.
function kelompokkanPerKelas(rows, classes, classIdKey = "classId") {
  const urutan = [...classes].sort((a, b) => {
    const ad = a.start_date ? new Date(a.start_date).getTime() : 0
    const bd = b.start_date ? new Date(b.start_date).getTime() : 0
    return ad - bd || (a.name || "").localeCompare(b.name || "")
  })
  const bucket = new Map()
  for (const row of rows) {
    const cid = row[classIdKey] || null
    if (!bucket.has(cid)) bucket.set(cid, [])
    bucket.get(cid).push(row)
  }
  const groups = []
  for (const k of urutan) {
    if (bucket.has(k.id)) {
      groups.push({ classId: k.id, className: k.name, rows: bucket.get(k.id) })
      bucket.delete(k.id)
    }
  }
  const tanpaKelas = [...(bucket.get(null) || []), ...[...bucket.entries()].filter(([cid]) => cid !== null).flatMap(([, r]) => r)]
  if (tanpaKelas.length) groups.push({ classId: null, className: null, rows: tanpaKelas })
  return groups
}

// `null` berarti "render datar seperti sebelum pengelompokan ini ada" — baik
// karena satu kelas sudah difilter, maupun karena program ini cuma punya satu
// kelompok untuk dikelompokkan (regresi nol untuk kasus itu).
function kelompokUntukTampilan(rows, classes, classIdKey, classId) {
  if (classId) return null
  const groups = kelompokkanPerKelas(rows, classes, classIdKey)
  return groups.length > 1 ? groups : null
}

// Kenapa seorang peserta belum layak sertifikat. Kode datang dari backend
// (reports.js → penghalangSertifikat) yang memakai ambang yang sama dengan
// penerbit sertifikat, jadi layar ini tidak pernah punya aturan sendiri.
function alasanBelumLayak(r, L) {
  const th = r.certThresholds || AMBANG_BAWAAN
  return (r.certBlockers || []).map((kode) =>
    kode === "POSTTEST_MISSING" ? L("Posttest belum dikerjakan", "Posttest not taken")
    : kode === "NOT_CONFIRMED" ? L("Menunggu konfirmasi sistem", "Awaiting system confirmation")
    : kode === "POSTTEST_LOW" ? L(`Posttest di bawah ${th.posttest}`, `Posttest below ${th.posttest}`)
    : L(`Kehadiran di bawah ${th.attendance}%`, `Attendance below ${th.attendance}%`)
  )
}

/* ── Potongan UI kecil ───────────────────────────────────────── */

function KpiRow({ items }) {
  return (
    <div className="report-kpis">
      {items.map((k) => (
        <div className="stat-card" key={k.label}>
          <div className="stat-label">{k.label}</div>
          <div className="stat-value">{k.value}</div>
          {k.sub && <div className="report-kpi-sub">{k.sub}</div>}
        </div>
      ))}
    </div>
  )
}

function ReportSkeleton() {
  return (
    <div aria-busy="true" aria-label="Loading">
      <div className="report-kpis">
        {[0, 1, 2, 3, 4].map((i) => (
          <div className="stat-card" key={i}>
            <div className="report-skel" style={{ height: 12, width: "55%" }} />
            <div className="report-skel" style={{ height: 24, width: "35%", marginTop: 12 }} />
          </div>
        ))}
      </div>
      <div className="tile">
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <div key={i} style={{ padding: "16px", borderBottom: "1px solid var(--border-subtle)" }}>
            <div className="report-skel" style={{ height: 14, width: `${70 - i * 6}%` }} />
          </div>
        ))}
      </div>
    </div>
  )
}

function BarKehadiran({ nilai, ambang }) {
  const pct = Math.max(0, Math.min(100, nilai ?? 0))
  return (
    <div className="report-bar-cell">
      <div className={`progress ${pct >= ambang ? "success" : "amber"}`}>
        <div className="fill" style={{ width: `${pct}%` }} />
      </div>
      <span className="pct mono">{Math.round(pct)}%</span>
    </div>
  )
}

function StatusSertifikat({ r, L }) {
  if (r.certEligible) {
    return <span className="tag green tiny"><span className="dot" />{L("Layak", "Eligible")}</span>
  }
  const alasan = alasanBelumLayak(r, L)
  return (
    <div>
      <span className="tag amber tiny"><span className="dot" />{L("Belum layak", "Not yet")}</span>
      {alasan.length > 0 && <div className="report-cert-reason">{alasan.join(" · ")}</div>}
    </div>
  )
}

const BelumTes = ({ L }) => <span className="pending">{L("Belum tes", "Not taken")}</span>

function GainCell({ gain }) {
  if (gain == null) return <span className="muted">—</span>
  const r = Math.round(gain)
  return <span className={r > 0 ? "report-gain-up" : r < 0 ? "report-gain-down" : ""}>{r >= 0 ? "+" : ""}{r}</span>
}

function TabelPeserta({ daftar, L, sort, onSort, showTtl, showCompany }) {
  const Th = ({ k, children, num, width }) => {
    const aktif = sort?.key === k
    return (
      <th
        className={`sortable${num ? " num" : ""}${aktif ? " sorted" : ""}`}
        style={width ? { width } : undefined}
        onClick={() => onSort(k)}
        aria-sort={aktif ? (sort.dir === "asc" ? "ascending" : "descending") : "none"}
      >
        {children}
        <span className="sort-mark">{aktif ? (sort.dir === "asc" ? "▲" : "▼") : "↕"}</span>
      </th>
    )
  }
  return (
    <TableWrap>
      <table className="table report-table">
        <thead>
          <tr>
            <Th k="nama">{L("Peserta", "Participant")}</Th>
            {showTtl && <th>{L("Tempat, Tgl Lahir", "Place, Date of Birth")}</th>}
            {showCompany && <Th k="perusahaan">{L("Perusahaan", "Company")}</Th>}
            <Th k="pretest" num width={90}>Pretest</Th>
            <Th k="posttest" num width={90}>Posttest</Th>
            <Th k="gain" num width={80}>Gain</Th>
            <Th k="kehadiran" width={190}>{L("Kehadiran", "Attendance")}</Th>
            <Th k="sertifikat" width={220}>{L("Sertifikat", "Certificate")}</Th>
          </tr>
        </thead>
        <tbody>
          {daftar.map((r) => (
            <tr key={r.userId}>
              <td>
                <div className="small" style={{ fontWeight: 500 }}>{r.name}</div>
                <div className="tiny muted">{r.email}</div>
              </td>
              {showTtl && <td className="small">{r.ttl || <span className="muted">—</span>}</td>}
              {showCompany && <td className="small">{r.company || <span className="muted">—</span>}</td>}
              <td className="mono small tnum num">{r.pretest != null ? Math.round(r.pretest) : <BelumTes L={L} />}</td>
              <td className="mono small tnum num">{r.posttest != null ? Math.round(r.posttest) : <BelumTes L={L} />}</td>
              <td className="mono small tnum num"><GainCell gain={r.gain} /></td>
              <td><BarKehadiran nilai={r.attendance} ambang={(r.certThresholds || AMBANG_BAWAAN).attendance} /></td>
              <td><StatusSertifikat r={r} L={L} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </TableWrap>
  )
}

function KosongDenganAksi({ children, aksi }) {
  return (
    <div className="report-empty muted small">
      <div>{children}</div>
      {aksi && <div style={{ marginTop: 10 }}>{aksi}</div>}
    </div>
  )
}

export default function ReportsScreen() {
  const t = useT()
  const { lang, role, currentProgram, currentProgramId, currentClass, currentClassId, setCurrentClassId, classes } = useApp()
  const orgName = useOrgName()
  const L = (id, en) => (lang === "id" ? id : en)

  const [tab, setTab] = React.useState("participants")
  const [rows, setRows] = React.useState([])
  const [courses, setCourses] = React.useState([])
  const [attendance, setAttendance] = React.useState([])
  const [loading, setLoading] = React.useState(false)
  const [loadError, setLoadError] = React.useState(false)
  const [reloadKey, setReloadKey] = React.useState(0)
  const [expanded, setExpanded] = React.useState(null)
  const [downloading, setDownloading] = React.useState(null)
  const [klien, setKlien] = React.useState("") // saringan perusahaan untuk laporan klien

  // Saringan per tab berbasis peserta. Barisnya disaring dan diurutkan SERVER
  // (lihat lib/reportFilters.js), jadi tabel ini dan berkas unduhan yang sama
  // tidak bisa berbeda isi. `rows` di atas tetap seluruh cakupan, untuk KPI,
  // hitungan segmen, daftar perusahaan, dan deretan huruf.
  // `lingkup` (program:kelas) disimpan BERSAMA saringannya: penyimpanan selalu
  // menulis ke cakupan milik saringan itu sendiri, jadi berpindah kelas tidak
  // bisa menulis saringan kelas lama ke kunci kelas baru.
  const [saringan, setSaringan] = React.useState({
    lingkup: null,
    ...Object.fromEntries(Object.entries(KOSONG_TAB).map(([t, k]) => [t, { ...k }])),
  })
  const [tampil, setTampil] = React.useState({ participants: null, compliance: null, attendance: null, courses: null })
  const [memuatTampil, setMemuatTampil] = React.useState(false)
  const [galatTampil, setGalatTampil] = React.useState(false)
  const permintaanRef = React.useRef(0)

  // Jadwal pengiriman hanya boleh dikelola admin (API menolak trainer).
  const bisaJadwal = role !== "trainer"

  // Daftar perusahaan diambil dari data peserta yang sudah dimuat layar ini —
  // tidak perlu endpoint tersendiri. Peserta yang tidak mengisi perusahaannya
  // tidak muncul di daftar, tapi tetap ikut pada pilihan "Semua perusahaan".
  const daftarPerusahaan = React.useMemo(() => {
    const nama = new Map() // huruf kecil → ejaan pertama yang ditemui
    for (const r of rows) {
      const c = (r.company || "").trim()
      if (c && !nama.has(c.toLowerCase())) nama.set(c.toLowerCase(), c)
    }
    return [...nama.values()].sort((a, b) => a.localeCompare(b, "id"))
  }, [rows])

  const adaTtl = React.useMemo(() => rows.some((r) => r.ttl), [rows])
  const adaPerusahaan = daftarPerusahaan.length > 0

  // Peserta hasil saringan server. Pengelompokan per kelas diputuskan dari data
  // LENGKAP (bukan hasil saringan), supaya judul kelas tidak hilang-muncul saat
  // mengetik di kotak pencarian.
  const pesertaTampil = tampil.participants ?? []
  const imoTampil = tampil.compliance ?? []
  const hurufHitung = React.useMemo(() => hitungHuruf(rows), [rows])

  const dikelompokkan = React.useMemo(
    () => kelompokUntukTampilan(rows, classes, "classId", currentClassId) != null,
    [rows, classes, currentClassId]
  )
  const participantGroups = React.useMemo(
    () => (dikelompokkan ? kelompokkanPerKelas(pesertaTampil, classes) : null),
    [dikelompokkan, pesertaTampil, classes]
  )
  const sesiTampil = tampil.attendance ?? []
  const courseTampil = tampil.courses ?? []
  // Sama seperti peserta: perlu-tidaknya dikelompokkan per kelas diputuskan dari
  // data LENGKAP, lalu yang dikelompokkan adalah hasil saringannya.
  const sesiDikelompokkan = React.useMemo(
    () => kelompokUntukTampilan(attendance, classes, "classId", currentClassId) != null,
    [attendance, classes, currentClassId]
  )
  const attendanceGroups = React.useMemo(
    () => (sesiDikelompokkan ? kelompokkanPerKelas(sesiTampil, classes) : null),
    [sesiDikelompokkan, sesiTampil, classes]
  )

  const jumlahLayak = React.useMemo(() => rows.filter((r) => r.certEligible).length, [rows])
  const sortPeserta = { key: saringan.participants.urut, dir: saringan.participants.arah }

  const ubahSaringan = (t) => (patch) =>
    setSaringan((s) => ({ ...s, [t]: { ...s[t], ...patch } }))
  // Mengosongkan saringan; urutan pilihan admin dibiarkan.
  const hapusSaringan = (t) => () =>
    setSaringan((s) => ({ ...s, [t]: { ...KOSONG_TAB[t], urut: s[t].urut, arah: s[t].arah } }))

  // Klik judul kolom: kolom yang sama membalik arah; kolom baru mulai dari
  // nama/perusahaan naik, angka turun (nilai tertinggi dulu).
  const onSort = (t) => (key) =>
    setSaringan((s) => {
      const f = s[t]
      const arah = f.urut === key
        ? (f.arah === "asc" ? "desc" : "asc")
        : (key === "nama" || key === "perusahaan" ? "asc" : "desc")
      return { ...s, [t]: { ...f, urut: key, arah } }
    })

  // Saringan bertahan per program+kelas+tab selama sesi peramban. Ganti kelas
  // memuat saringan milik kelas itu (kosong bila belum pernah diatur).
  React.useEffect(() => {
    if (!currentProgramId) return
    const lingkup = lingkupSaringan(currentProgramId, currentClassId)
    setSaringan({
      lingkup,
      ...Object.fromEntries(Object.entries(KOSONG_TAB).map(([t, k]) => [t, bacaSaringan(lingkup, t, k)])),
    })
  }, [currentProgramId, currentClassId])

  React.useEffect(() => {
    if (!saringan.lingkup) return
    for (const t of Object.keys(KOSONG_TAB)) simpanSaringan(saringan.lingkup, t, saringan[t])
  }, [saringan])

  // Baris yang tampil = jawaban server untuk saringan tab aktif. Hanya jawaban
  // TERAKHIR yang dipakai: klik huruf beruntun tidak boleh berakhir dengan
  // tabel milik klik sebelumnya yang kebetulan tiba belakangan.
  const tabSaring = JENIS_TAB[tab] ? tab : null
  // Rentang tanggal terbalik tidak dikirim (server menolaknya); bilah saringan
  // sudah menyebut sebabnya, dan tabel menahan hasil terakhir yang sah.
  const queryTampil = tabSaring && !rentangSalah(saringan[tabSaring])
    ? JSON.stringify(keQuery(saringan[tabSaring], JENIS_TAB[tabSaring]))
    : ""
  React.useEffect(() => {
    if (!currentProgramId || !tabSaring || !queryTampil) return
    const ambilBaris = { progress: programProgress, sesi: programAttendance, course: programCoursesProgress }[JENIS_TAB[tabSaring]]
    const id = ++permintaanRef.current
    setMemuatTampil(true)
    setGalatTampil(false)
    ambilBaris(currentProgramId, currentClassId, JSON.parse(queryTampil))
      .then((data) => { if (id === permintaanRef.current) setTampil((t) => ({ ...t, [tabSaring]: data })) })
      .catch(() => { if (id === permintaanRef.current) setGalatTampil(true) })
      .finally(() => { if (id === permintaanRef.current) setMemuatTampil(false) })
  }, [currentProgramId, currentClassId, tabSaring, queryTampil, reloadKey])

  // Kelas diambil dari pemilih Kelas yang sama dengan sidebar (konteks yang
  // sama dengan Jadwal, Learning Gain, dan Peserta) — memilih di sini sama
  // dengan memilih di sana. Dulu hanya tab Kepatuhan IMO yang punya saringan
  // kelas; tab lain diam-diam merekap seluruh program.
  React.useEffect(() => {
    if (!currentProgramId) return
    let batal = false
    // Perusahaan yang dipilih milik program/kelas sebelumnya. Kalau dibiarkan,
    // tombol unduh meminta laporan untuk perusahaan yang tidak ada di kelas
    // ini — dan hasilnya berkas kosong tanpa penjelasan.
    setKlien("")
    setLoading(true)
    setLoadError(false)
    let gagal = false
    // Kegagalan tidak lagi disamarkan sebagai "belum ada peserta": ditandai
    // dan ditampilkan dengan tombol coba lagi.
    const ambil = (p) => p.catch(() => { gagal = true; return [] })
    Promise.all([
      ambil(programProgress(currentProgramId, currentClassId)),
      ambil(programCoursesProgress(currentProgramId, currentClassId)),
      ambil(programAttendance(currentProgramId, currentClassId)),
    ])
      .then(([p, c, a]) => {
        if (batal) return
        setRows(p); setCourses(c); setAttendance(a); setLoadError(gagal)
      })
      .finally(() => { if (!batal) setLoading(false) })
    return () => { batal = true }
  }, [currentProgramId, currentClassId, reloadKey])

  // Berkas mana memakai saringan tab mana, dan sebagai jenis laporan apa di
  // server (kunci yang berlaku berbeda per jenis; yang lain ditolak server).
  const saringanUnduhan = (kind) =>
    kind.startsWith("participants") ? keQuery(saringan.participants, "progress")
    : kind.startsWith("jawaban-") ? keQuery(saringan.participants, "orang")
    : kind.startsWith("compliance") ? keQuery(saringan.compliance, "imo")
    : kind.startsWith("courses") ? keQuery(saringan.courses, "course")
    // Matriks tidak mengikuti saringan tab Kehadiran: `q` di sini mencari SESI,
    // sedangkan matriks menafsirkan `q` sebagai nama peserta.
    : kind.startsWith("attendance-matrix") ? null
    : kind.startsWith("attendance-detail") ? keQuery(saringan.attendance, "pemindaian")
    : kind.startsWith("attendance") ? keQuery(saringan.attendance, "sesi")
    : null

  const handleDownload = async (kind) => {
    setDownloading(kind)
    try {
      const kelas = currentClassId || undefined
      const fP = keQuery(saringan.participants, "progress")
      const fO = keQuery(saringan.participants, "orang")
      const fI = keQuery(saringan.compliance, "imo")
      const fC = keQuery(saringan.courses, "course")
      const fS = keQuery(saringan.attendance, "sesi")
      const fD = keQuery(saringan.attendance, "pemindaian")
      const fn = {
        "participants-csv": (pid) => downloadProgressCsv(pid, kelas, fP),
        "participants-pdf": (pid) => downloadProgressPdf(pid, kelas, fP),
        "participants-xlsx": (pid) => downloadProgressXlsx(pid, kelas, fP),
        "courses-csv": (pid) => downloadCoursesProgressCsv(pid, kelas, fC),
        "courses-pdf": (pid) => downloadCoursesProgressPdf(pid, kelas, fC),
        "courses-xlsx": (pid) => downloadCoursesProgressXlsx(pid, kelas, fC),
        "attendance-csv": (pid) => downloadAttendanceCsv(pid, kelas, fS),
        "attendance-pdf": (pid) => downloadAttendancePdf(pid, kelas, fS),
        "attendance-xlsx": (pid) => downloadAttendanceXlsx(pid, kelas, fS),
        "attendance-detail-csv": (pid) => downloadAttendanceDetailCsv(pid, kelas, fD),
        "attendance-detail-xlsx": (pid) => downloadAttendanceDetailXlsx(pid, kelas, fD),
        "attendance-detail-pdf": (pid) => downloadAttendanceDetailPdf(pid, kelas, fD),
        "attendance-matrix-csv": (pid) => downloadAttendanceMatrixCsv(pid, kelas),
        "attendance-matrix-xlsx": (pid) => downloadAttendanceMatrixXlsx(pid, kelas),
        "attendance-matrix-pdf": (pid) => downloadAttendanceMatrixPdf(pid, kelas),
        "compliance-csv": (pid) => downloadImoComplianceCsv(pid, kelas, fI),
        "compliance-pdf": (pid) => downloadImoCompliancePdf(pid, kelas, fI),
        "compliance-xlsx": (pid) => downloadImoComplianceXlsx(pid, kelas, fI),
        "client-csv": (pid) => downloadClientReportCsv(pid, klien || undefined, kelas),
        "client-pdf": (pid) => downloadClientReportPdf(pid, klien || undefined, kelas),
        "client-xlsx": (pid) => downloadClientReportXlsx(pid, klien || undefined, kelas),
        "jawaban-pretest-csv": (pid) => downloadTestAnswersCsv(pid, "pretest", kelas, fO),
        "jawaban-pretest-xlsx": (pid) => downloadTestAnswersXlsx(pid, "pretest", kelas, fO),
        "jawaban-posttest-csv": (pid) => downloadTestAnswersCsv(pid, "posttest", kelas, fO),
        "jawaban-posttest-xlsx": (pid) => downloadTestAnswersXlsx(pid, "posttest", kelas, fO),
      }[kind]
      if (!fn) return
      const blob = await fn(currentProgramId)
      // Diturunkan dari akhiran `kind`, bukan ditebak biner csv/pdf: dulu
      // `kind.endsWith("pdf") ? "pdf" : "csv"` membuat berkas Excel terunduh
      // bernama .csv dan ditolak Excel.
      const ext = kind.split("-").pop()
      const base = kind.startsWith("jawaban-")
        ? kind.replace(/-(csv|pdf|xlsx)$/, "")
        : kind.startsWith("client")
        ? `laporan-pelatihan${klien ? `-${klien.toLowerCase().replace(/[^a-z0-9]+/g, "-")}` : ""}`
        : kind.startsWith("participants")
        ? "progres-peserta"
        : kind.startsWith("courses")
          ? "progres-course"
          : kind.startsWith("compliance")
            ? "imo-compliance"
            : kind.startsWith("attendance-detail")
              ? "kehadiran-peserta"
              : kind.startsWith("attendance-matrix")
                ? "rekap-kehadiran"
                : "kehadiran-sesi"
      const url = URL.createObjectURL(blob)
      const a = document.createElement("a")
      a.href = url
      // Nama kelas ikut ke nama berkas, untuk semua laporan — tiga angkatan
      // yang diunduh berturut-turut tidak boleh saling menimpa di folder unduhan.
      const kelasSlug = currentClass?.name
        ? `-${currentClass.name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`
        : ""
      // Sama dengan nama yang diberikan server: berkas tersaring menyatakan
      // dirinya tersaring, supaya daftar sebagian tidak disangka daftar lengkap.
      const f = saringanUnduhan(kind)
      const disaring = f && KUNCI_SARING.some((k) => f[k]) ? "-disaring" : ""
      a.download = `${base}${disaring}-${currentProgram?.code || "program"}${kelasSlug}.${ext}`
      a.click()
      URL.revokeObjectURL(url)
    } catch (e) {
      if (e?.response?.status === 503) {
        toast.error(L("Ekspor PDF sedang tidak tersedia", "PDF export unavailable"))
      } else {
        toast.error(e?.response?.data?.error || e.message)
      }
    } finally {
      setDownloading(null)
    }
  }

  if (!currentProgramId) {
    return (
      <div className="main-inner">
        <div className="page-header">
          <div className="page-header-text">
            <h1 className="h1">{t.reports}</h1>
            <div className="muted mt-1">{L("Pilih program terlebih dahulu.", "Select a program first.")}</div>
          </div>
        </div>
      </div>
    )
  }

  // Jawaban tes tidak membawa nilai dan status sertifikat; saringan semacam itu
  // tidak bisa ikut ke berkasnya, dan admin harus tahu sebelum mengunduh.
  const tidakIkutJawaban = tidakIkut(saringan.participants, "orang")
  const catatanJawaban = tidakIkutJawaban.length
    ? L(" Hanya mengikuti pencarian, huruf, dan perusahaan; saringan lain tidak berlaku untuk berkas ini.",
        " Follows search, letter, and company only; other filters do not apply to this file.")
    : ""

  // Persentase di matriks selalu atas seluruh sesi; menyaringnya menurut sesi
  // akan membuat peserta tampak memenuhi ambang dari beberapa sesi saja.
  const catatanMatriks = jumlahAktif(saringan.attendance) > 0
    ? L(" Tidak mengikuti saringan sesi: kolom dan persentasenya selalu atas seluruh sesi.",
        " Does not follow the session filters: columns and percentages always cover all sessions.")
    : ""

  // Semua berkas yang bisa dibuat dari tab aktif — satu menu, satu tempat.
  const FORMAT_LENGKAP = ["csv", "xlsx", "pdf"]
  const bagianUnduh = {
    participants: [
      {
        key: "participants", kindPrefix: "participants", formats: FORMAT_LENGKAP,
        title: L("Progres peserta", "Participant progress"),
        hint: L("Tabel ini: nilai, kehadiran, dan status sertifikat.", "This table: scores, attendance, certificate status."),
      },
      {
        key: "pretest", kindPrefix: "jawaban-pretest", formats: ["csv", "xlsx"],
        title: L("Jawaban pretest per soal", "Pretest answers per question"),
        hint: L("Satu kolom per soal; benar hijau, salah merah. Skor manual tidak punya jawaban dan ditandai di kolom Status.",
                "One column per question; correct green, wrong red. Manual scores have no answers and are flagged in Status.") + catatanJawaban,
      },
      {
        key: "posttest", kindPrefix: "jawaban-posttest", formats: ["csv", "xlsx"],
        title: L("Jawaban posttest per soal", "Posttest answers per question"),
        hint: L("Sama seperti pretest; teks soal lengkap ada di lembar kedua.", "Same as pretest; full question text is on the second sheet.") + catatanJawaban,
      },
    ],
    courses: [
      {
        key: "courses", kindPrefix: "courses", formats: FORMAT_LENGKAP,
        title: L("Progres per course", "Per-course progress"),
        hint: L("Penyelesaian tiap course beserta rincian pesertanya.", "Completion of each course with participant breakdown."),
      },
    ],
    attendance: [
      {
        key: "attendance", kindPrefix: "attendance", formats: FORMAT_LENGKAP,
        title: L("Rekap per sesi", "Per-session summary"),
        hint: L("Tabel ini: jumlah hadir dan ditandai tiap sesi.", "This table: attended and flagged counts per session."),
      },
      {
        key: "matrix", kindPrefix: "attendance-matrix", formats: FORMAT_LENGKAP,
        title: L("Rekap per peserta", "Per-participant summary"),
        hint: L("Satu baris per peserta, satu kolom per sesi, plus persentase dan status kelulusan — untuk melihat siapa yang kurang hadir.",
                "One row per participant, one column per session, plus percentage and pass status — to see who is short on attendance.") + catatanMatriks,
      },
      {
        key: "detail", kindPrefix: "attendance-detail", formats: FORMAT_LENGKAP,
        title: L("Rincian pemindaian", "Scan detail"),
        hint: L("Satu baris per pemindaian: tanggal, sesi, nama, perusahaan, waktu.", "One row per scan: date, session, name, company, time."),
      },
    ],
    client: [
      {
        key: "client", kindPrefix: "client", formats: FORMAT_LENGKAP,
        title: L("Laporan pelaksanaan pelatihan", "Training delivery report"),
        hint: klien
          ? L(`Hanya peserta ${klien}.`, `Only ${klien}'s participants.`)
          : L("Semua perusahaan, dikelompokkan per perusahaan.", "All companies, grouped by company."),
      },
    ],
    compliance: [
      {
        key: "compliance", kindPrefix: "compliance", formats: FORMAT_LENGKAP,
        title: L("Laporan kepatuhan IMO / Hubla", "IMO / Hubla compliance report"),
        hint: L("Per peserta: data maritim, nilai, kehadiran, dan nomor sertifikat.", "Per participant: maritime data, scores, attendance, certificate number."),
      },
    ],
  }[tab]

  const namaCakupan = currentClass?.name
    ? `${L("Kelas", "Class")} ${currentClass.name}`
    : L("Semua kelas (seluruh program)", "All classes (whole program)")

  const saringanTab = tabSaring ? saringan[tabSaring] : null
  const saringanAktifTab = saringanTab ? jumlahAktif(saringanTab) > 0 : false
  const jumlahTampilTab = tabSaring ? (tampil[tabSaring] ?? []).length : 0
  const totalTab = tab === "attendance" ? attendance.length : tab === "courses" ? courses.length : rows.length
  const satuanTab = tab === "attendance" ? ["sesi", "sessions"] : tab === "courses" ? ["course", "courses"] : ["peserta", "participants"]
  // Tab Kehadiran tidak dinonaktifkan saat hasilnya kosong: rekap per peserta
  // di menu yang sama tidak bergantung pada saringan sesi.
  const kosongKarenaSaringan = saringanAktifTab && tab !== "attendance" && !memuatTampil && !galatTampil && jumlahTampilTab === 0
  const rentangTerbalik = tab === "attendance" && rentangSalah(saringan.attendance)

  // ── KPI per tab ──────────────────────────────────────────────
  const kpiPeserta = [
    { label: L("Peserta", "Participants"), value: rows.length },
    {
      label: L("Rata-rata pretest", "Avg pretest"),
      value: bulat(rata(rows.map((r) => r.pretest))),
      sub: L(`${rows.filter((r) => r.pretest != null).length} sudah tes`, `${rows.filter((r) => r.pretest != null).length} taken`),
    },
    {
      label: L("Rata-rata posttest", "Avg posttest"),
      value: bulat(rata(rows.map((r) => r.posttest))),
      sub: L(`${rows.filter((r) => r.posttest != null).length} sudah tes`, `${rows.filter((r) => r.posttest != null).length} taken`),
    },
    {
      label: L("Rata-rata gain", "Avg gain"),
      value: (() => { const g = rata(rows.map((r) => r.gain)); return g == null ? "—" : `${g >= 0 ? "+" : ""}${Math.round(g)}` })(),
      sub: L("posttest − pretest", "posttest − pretest"),
    },
    { label: L("Kehadiran rata-rata", "Avg attendance"), value: rows.length ? `${Math.round(rata(rows.map((r) => r.attendance ?? 0)))}%` : "—" },
    {
      label: L("Layak sertifikat", "Cert. eligible"),
      value: `${jumlahLayak}/${rows.length}`,
      sub: rows.length ? `${Math.round((jumlahLayak / rows.length) * 100)}%` : undefined,
    },
  ]

  const tabs = [
    { id: "participants", label: L("Progres Peserta", "Participant Progress"), count: loading ? undefined : rows.length },
    { id: "courses", label: L("Progres per Course", "Per-Course Progress"), count: loading ? undefined : courses.length },
    { id: "attendance", label: L("Kehadiran Sesi", "Session Attendance"), count: loading ? undefined : attendance.length },
    { id: "client", label: L("Laporan Klien", "Client Report") },
    { id: "compliance", label: L("Kepatuhan IMO", "IMO Compliance") },
    ...(bisaJadwal ? [{ id: "schedules", label: L("Jadwal Kirim", "Scheduled") }] : []),
  ]

  return (
    <div className="main-inner">
      <div className="page-header">
        <div className="page-header-text">
          <div className="eyebrow">
            {currentProgram?.name || currentProgram?.code?.toUpperCase() || orgName} · {L("Laporan", "Reports")}
          </div>
          <h1 className="h1" style={{ marginTop: 6 }}>{t.reports}</h1>
          <div className="muted mt-1">
            {L("Pantau progres peserta, course & kehadiran, lalu unduh sebagai CSV, Excel, atau PDF.",
               "Track participant, course & attendance progress, then download as CSV, Excel, or PDF.")}
          </div>
        </div>
        <div className="page-header-actions" style={{ alignItems: "flex-end", flexWrap: "wrap" }}>
          {classes.length > 0 && (
            <label className="report-class-select">
              <span>{L("Kelas", "Class")}</span>
              <select
                className="input"
                value={currentClassId || ""}
                onChange={(e) => setCurrentClassId(e.target.value || null)}
              >
                <option value="">{L("Semua kelas", "All classes")}</option>
                {classes.map((k) => <option key={k.id} value={k.id}>{k.name}</option>)}
              </select>
            </label>
          )}
          {bagianUnduh && (
            <ExportMenuGroups
              sections={bagianUnduh}
              busyKind={downloading}
              onSelect={handleDownload}
              lang={lang}
              disabled={kosongKarenaSaringan || rentangTerbalik}
              disabledTitle={rentangTerbalik
                ? L("Perbaiki rentang tanggal dulu.", "Fix the date range first.")
                : L("Tidak ada yang cocok dengan saringan.", "Nothing matches the filters.")}
              footer={<>
                {L("Cakupan berkas: ", "File scope: ")}<strong>{namaCakupan}</strong>
                {saringanAktifTab && (
                  <div style={{ marginTop: 4 }}>
                    {L("Saringan aktif: ", "Active filters: ")}
                    <strong>{labelAktif(saringanTab, L).map((c) => c.label).join(" · ")}</strong>
                    {` · ${L(`${jumlahTampilTab} dari ${totalTab} ${satuanTab[0]}`, `${jumlahTampilTab} of ${totalTab} ${satuanTab[1]}`)}`}
                  </div>
                )}
              </>}
            />
          )}
        </div>
      </div>

      <Tabs tabs={tabs} value={tab} onChange={setTab} />

      {loadError && tab !== "schedules" && !loading && (
        <div className="report-error" role="alert">
          <Icon name="alert" size={16} color="var(--amber)" />
          <span>{L("Sebagian data laporan gagal dimuat, jadi angka di bawah bisa tidak lengkap.", "Some report data failed to load, so the figures below may be incomplete.")}</span>
          <Btn size="sm" onClick={() => setReloadKey((k) => k + 1)}>{L("Coba lagi", "Retry")}</Btn>
        </div>
      )}

      {tab === "schedules" && bisaJadwal ? (
        <ReportSchedulesTab pid={currentProgramId} lang={lang} />
      ) : loading ? (
        <ReportSkeleton />
      ) : tab === "participants" ? (
        <>
          <KpiRow items={kpiPeserta} />
          <div className="tile">
            <div className="tile-header">
              <h3>{L("Progres Peserta", "Participant Progress")}</h3>
              <span className="tile-header-sub">{rows.length}</span>
            </div>
            {rows.length === 0 ? (
              <KosongDenganAksi>{L("Belum ada peserta di cakupan ini.", "No participants in this scope.")}</KosongDenganAksi>
            ) : (
              <>
                <ReportFilterBar
                  L={L}
                  saringan={saringan.participants}
                  ubah={ubahSaringan("participants")}
                  hapusSemua={hapusSaringan("participants")}
                  hurufHitung={hurufHitung}
                  perusahaan={daftarPerusahaan}
                  hitung={{ semua: rows.length, layak: jumlahLayak, belum: rows.length - jumlahLayak }}
                  jumlah={{ tampil: pesertaTampil.length, total: rows.length }}
                  lainnya={["halangan", "tes", "hadir", "identitas"]}
                />
                {galatTampil ? (
                  <KosongDenganAksi aksi={<Btn size="sm" onClick={() => setReloadKey((k) => k + 1)}>{L("Coba lagi", "Retry")}</Btn>}>
                    {L("Hasil saringan gagal dimuat.", "Filtered results failed to load.")}
                  </KosongDenganAksi>
                ) : tampil.participants === null ? (
                  <KosongDenganAksi>{L("Memuat…", "Loading…")}</KosongDenganAksi>
                ) : pesertaTampil.length === 0 ? (
                  <KosongDenganAksi
                    aksi={<Btn size="sm" onClick={hapusSaringan("participants")}>{L("Hapus saringan", "Clear filters")}</Btn>}
                  >
                    {L("Tidak ada peserta yang cocok dengan pencarian atau saringan ini.", "No participants match this search or filter.")}
                  </KosongDenganAksi>
                ) : (
                  <div className={memuatTampil ? "report-dimmed" : undefined} aria-busy={memuatTampil}>
                    {participantGroups ? (
                      participantGroups.map((g) => (
                        <div key={g.classId || "tanpa-kelas"}>
                          <div className="report-group-head">
                            {g.className || L("Tanpa kelas", "No class")}
                            <span className="tiny muted" style={{ fontWeight: 400 }}>· {g.rows.length}</span>
                          </div>
                          <TabelPeserta daftar={g.rows} L={L} sort={sortPeserta} onSort={onSort("participants")} showTtl={adaTtl} showCompany={adaPerusahaan} />
                        </div>
                      ))
                    ) : (
                      <TabelPeserta daftar={pesertaTampil} L={L} sort={sortPeserta} onSort={onSort("participants")} showTtl={adaTtl} showCompany={adaPerusahaan} />
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </>
      ) : tab === "courses" ? (
        <>
          <KpiRow
            items={[
              { label: "Course", value: courses.length },
              { label: L("Rata-rata selesai", "Avg completion"), value: courses.length ? `${Math.round(rata(courses.map((c) => c.avgCompletionPct)))}%` : "—" },
              { label: L("Pelajaran", "Lessons"), value: courses.reduce((n, c) => n + (c.lessonCount || 0), 0) },
            ]}
          />
          <div className="tile">
            <div className="tile-header">
              <h3>{L("Progres per Course", "Per-Course Progress")}</h3>
              <span className="tile-header-sub">{courses.length}</span>
            </div>
            {courses.length === 0 ? (
              <KosongDenganAksi>{L("Belum ada course.", "No courses.")}</KosongDenganAksi>
            ) : (<>
              <RingkasFilterBar
                L={L}
                saringan={saringan.courses}
                ubah={ubahSaringan("courses")}
                hapusSemua={hapusSaringan("courses")}
                jumlah={{ tampil: courseTampil.length, total: courses.length, satuan: ["course", "courses"] }}
                placeholder={L("Cari judul course", "Search course title")}
                urutan={URUTAN_COURSE}
              />
              {galatTampil ? (
                <KosongDenganAksi aksi={<Btn size="sm" onClick={() => setReloadKey((k) => k + 1)}>{L("Coba lagi", "Retry")}</Btn>}>
                  {L("Hasil saringan gagal dimuat.", "Filtered results failed to load.")}
                </KosongDenganAksi>
              ) : tampil.courses === null ? (
                <KosongDenganAksi>{L("Memuat…", "Loading…")}</KosongDenganAksi>
              ) : courseTampil.length === 0 ? (
                <KosongDenganAksi aksi={<Btn size="sm" onClick={hapusSaringan("courses")}>{L("Hapus saringan", "Clear filters")}</Btn>}>
                  {L("Tidak ada course yang cocok dengan pencarian ini.", "No courses match this search.")}
                </KosongDenganAksi>
              ) : (
              <div className={memuatTampil ? "report-dimmed" : undefined} aria-busy={memuatTampil}>
              {courseTampil.map((c) => {
                const isOpen = expanded === c.courseId
                return (
                  <div key={c.courseId} style={{ borderBottom: "1px solid var(--border)" }}>
                    <div
                      className="row"
                      style={{ padding: "14px 16px", gap: 16, cursor: "pointer" }}
                      onClick={() => setExpanded(isOpen ? null : c.courseId)}
                      role="button"
                      tabIndex={0}
                      aria-expanded={isOpen}
                      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setExpanded(isOpen ? null : c.courseId) } }}
                    >
                      <Icon name={isOpen ? "chevron_down" : "chevron_right"} size={14} color="var(--text-tertiary)" />
                      <div className="col" style={{ flex: 1, minWidth: 0 }}>
                        <span style={{ fontWeight: 500 }}>{c.title}</span>
                        <span className="tiny muted mt-1">
                          {c.lessonCount} {L("pelajaran", "lessons")} · {c.totalParticipants} {L("peserta", "participants")}
                        </span>
                      </div>
                      <div style={{ width: 160 }}>
                        <div className="row between mb-1">
                          <span className="tiny muted">{L("Rata-rata selesai", "Avg complete")}</span>
                          <span className="tiny mono">{c.avgCompletionPct}%</span>
                        </div>
                        <div className="progress"><div className="fill" style={{ width: `${c.avgCompletionPct}%` }} /></div>
                      </div>
                    </div>
                    {isOpen && (
                      <div style={{ background: "var(--g-10)", padding: "4px 0" }}>
                        {c.participants.length === 0 ? (
                          <div className="muted small" style={{ textAlign: "center", padding: 12 }}>
                            {L("Belum ada peserta.", "No participants.")}
                          </div>
                        ) : (() => {
                          const tabelPesertaCourse = (daftar) => (
                            <TableWrap>
                              <table className="table table-compact">
                                <thead><tr>
                                  <th>{L("Peserta", "Participant")}</th>
                                  <th>{L("Modul selesai", "Modules done")}</th>
                                  <th>{L("% Selesai", "% Complete")}</th>
                                  <th>Posttest</th>
                                </tr></thead>
                                <tbody>
                                  {daftar.map((p) => (
                                    <tr key={p.userId}>
                                      <td>
                                        <div className="small" style={{ fontWeight: 500 }}>{p.name}</div>
                                        <div className="tiny muted">{p.email}</div>
                                        {p.ttl && <div className="tiny muted">{p.ttl}</div>}
                                      </td>
                                      <td className="mono small tnum">{p.completedLessons} / {c.lessonCount}</td>
                                      <td className="mono small tnum">{Math.round(p.completionPct)}%</td>
                                      <td className="mono small tnum">{p.posttestScore != null ? Math.round(p.posttestScore) : <BelumTes L={L} />}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </TableWrap>
                          )
                          // Dikelompokkan per kelas DI DALAM course ini — course-nya
                          // sendiri tidak diulang, cuma pesertanya yang dipecah.
                          const kelompok = kelompokUntukTampilan(c.participants, classes, "classId", currentClassId)
                          return kelompok
                            ? kelompok.map((g) => (
                                <div key={g.classId || "tanpa-kelas"}>
                                  <div className="tiny muted" style={{ fontWeight: 600, padding: "6px 16px 2px" }}>
                                    {g.className || L("Tanpa kelas", "No class")}
                                    {" · "}{g.rows.length}
                                  </div>
                                  {tabelPesertaCourse(g.rows)}
                                </div>
                              ))
                            : tabelPesertaCourse(c.participants)
                        })()}
                      </div>
                    )}
                  </div>
                )
              })}
              </div>
              )}
            </>)}
          </div>
        </>
      ) : tab === "attendance" ? (
        <>
          <KpiRow
            items={[
              { label: L("Sesi", "Sessions"), value: attendance.length },
              { label: L("Total kehadiran", "Total attendance"), value: attendance.reduce((n, s) => n + (s.attended || 0), 0) },
              {
                label: L("Rata-rata hadir/sesi", "Avg attended/session"),
                value: attendance.length ? bulat(rata(attendance.map((s) => s.attended || 0))) : "—",
              },
              {
                label: L("Ditandai", "Flagged"),
                value: attendance.reduce((n, s) => n + (s.flagged || 0), 0),
                sub: L("pemindaian perlu dicek", "scans to review"),
              },
            ]}
          />
          <div className="tile">
            <div className="tile-header">
              <h3>{L("Kehadiran Sesi", "Session Attendance")}</h3>
              <span className="tile-header-sub">{attendance.length}</span>
            </div>
            {attendance.length === 0 ? (
              <KosongDenganAksi>{L("Belum ada sesi.", "No sessions.")}</KosongDenganAksi>
            ) : (<>
              <RingkasFilterBar
                L={L}
                saringan={saringan.attendance}
                ubah={ubahSaringan("attendance")}
                hapusSemua={hapusSaringan("attendance")}
                jumlah={{ tampil: sesiTampil.length, total: attendance.length, satuan: ["sesi", "sessions"] }}
                placeholder={L("Cari judul sesi, trainer, atau lokasi", "Search session, trainer, or location")}
                urutan={URUTAN_SESI}
                tanggal
                ditandai
              />
              {galatTampil ? (
                <KosongDenganAksi aksi={<Btn size="sm" onClick={() => setReloadKey((k) => k + 1)}>{L("Coba lagi", "Retry")}</Btn>}>
                  {L("Hasil saringan gagal dimuat.", "Filtered results failed to load.")}
                </KosongDenganAksi>
              ) : tampil.attendance === null ? (
                <KosongDenganAksi>{L("Memuat…", "Loading…")}</KosongDenganAksi>
              ) : sesiTampil.length === 0 ? (
                <KosongDenganAksi aksi={<Btn size="sm" onClick={hapusSaringan("attendance")}>{L("Hapus saringan", "Clear filters")}</Btn>}>
                  {L("Tidak ada sesi yang cocok dengan saringan ini.", "No sessions match these filters.")}
                </KosongDenganAksi>
              ) : (
              <div className={memuatTampil ? "report-dimmed" : undefined} aria-busy={memuatTampil}>
              {(() => {
                const tabelSesi = (daftar) => (
                  <TableWrap>
                    <table className="table report-table">
                      <thead><tr>
                        <th>{L("Sesi", "Session")}</th>
                        <th>{L("Jadwal", "Schedule")}</th>
                        <th>{L("Lokasi", "Location")}</th>
                        <th>Trainer</th>
                        <th className="num">{L("Hadir", "Attended")}</th>
                        <th className="num">{L("Ditandai", "Flagged")}</th>
                      </tr></thead>
                      <tbody>
                        {daftar.map((s) => (
                          <tr key={s.sessionId}>
                            <td className="small" style={{ fontWeight: 500 }}>{s.title}</td>
                            <td className="mono small">
                              {new Date(s.scheduledAt).toLocaleString(lang === "id" ? "id-ID" : "en-US", {
                                day: "numeric", month: "short", hour: "2-digit", minute: "2-digit",
                              })}
                            </td>
                            <td className="small">{s.location || "—"}</td>
                            <td className="small">{s.trainer || "—"}</td>
                            <td className="mono small tnum num">{s.attended}</td>
                            <td className="mono small tnum num">
                              {s.flagged > 0 ? <span className="tag amber tiny">{s.flagged}</span> : "0"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </TableWrap>
                )
                return attendanceGroups
                  ? attendanceGroups.map((g) => (
                      <div key={g.classId || "tanpa-kelas"}>
                        <div className="report-group-head">
                          {g.className || L("Tanpa kelas", "No class")}
                          <span className="tiny muted" style={{ fontWeight: 400 }}>· {g.rows.length}</span>
                        </div>
                        {tabelSesi(g.rows)}
                      </div>
                    ))
                  : tabelSesi(sesiTampil)
              })()}
              </div>
              )}
            </>)}
            <div className="report-note">
              {L("Butuh daftar siapa hadir di sesi mana? Unduh “Rekap per peserta” atau “Rincian pemindaian” dari menu Unduh laporan.",
                 "Need who attended which session? Download “Per-participant summary” or “Scan detail” from the Download menu.")}
            </div>
          </div>
        </>
      ) : tab === "client" ? (
        <ClientTab
          L={L}
          rows={rows}
          klien={klien}
          setKlien={setKlien}
          daftarPerusahaan={daftarPerusahaan}
        />
      ) : tab === "compliance" ? (
        <ComplianceTab
          L={L}
          rows={rows}
          daftar={imoTampil}
          memuat={memuatTampil}
          galat={galatTampil}
          belumDimuat={tampil.compliance === null}
          onRetry={() => setReloadKey((k) => k + 1)}
          onHapus={hapusSaringan("compliance")}
          bilah={
            <ReportFilterBar
              L={L}
              saringan={saringan.compliance}
              ubah={ubahSaringan("compliance")}
              hapusSemua={hapusSaringan("compliance")}
              hurufHitung={hurufHitung}
              perusahaan={daftarPerusahaan}
              hitung={{ semua: rows.length, layak: jumlahLayak, belum: rows.length - jumlahLayak }}
              jumlah={{ tampil: imoTampil.length, total: rows.length }}
              lainnya={["identitas", "tes", "hadir"]}
            />
          }
        />
      ) : null}
    </div>
  )
}

/* ── Tab Laporan Klien ────────────────────────────────────────── */
function ClientTab({ L, rows, klien, setKlien, daftarPerusahaan }) {
  const terpilih = React.useMemo(
    () => (klien ? rows.filter((r) => (r.company || "").trim().toLowerCase() === klien.toLowerCase()) : rows),
    [rows, klien]
  )
  const perPerusahaan = React.useMemo(() => {
    const peta = new Map()
    for (const r of terpilih) {
      const nama = (r.company || "").trim()
      if (!peta.has(nama)) peta.set(nama, [])
      peta.get(nama).push(r)
    }
    return [...peta.entries()]
      .map(([nama, rs]) => ({
        nama,
        n: rs.length,
        pretest: rata(rs.map((r) => r.pretest)),
        posttest: rata(rs.map((r) => r.posttest)),
        kehadiran: rata(rs.map((r) => r.attendance ?? 0)),
        layak: rs.filter((r) => r.certEligible).length,
      }))
      .sort((a, b) => (a.nama === "") - (b.nama === "") || a.nama.localeCompare(b.nama, "id"))
  }, [terpilih])

  return (
    <div className="tile">
      <div className="tile-header">
        <h3>{L("Laporan Pelaksanaan Pelatihan", "Training Delivery Report")}</h3>
        <span className="tile-header-sub">{terpilih.length} {L("peserta", "participants")}</span>
      </div>
      <div className="report-toolbar">
        <label className="report-class-select">
          <span>{L("Perusahaan", "Company")}</span>
          <select className="input" value={klien} onChange={(e) => setKlien(e.target.value)}>
            <option value="">{L("Semua perusahaan", "All companies")}</option>
            {daftarPerusahaan.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </label>
        <span className="small muted" style={{ flex: 1, minWidth: 220 }}>
          {klien
            ? L("Hanya peserta perusahaan ini yang masuk ke berkas.", "Only this company's participants are included.")
            : L("Seluruh peserta, dikelompokkan per perusahaan — untuk arsip Anda sendiri.", "All participants, grouped by company — for your own records.")}
        </span>
      </div>
      {perPerusahaan.length === 0 ? (
        <KosongDenganAksi>{L("Belum ada peserta di cakupan ini.", "No participants in this scope.")}</KosongDenganAksi>
      ) : (
        <TableWrap>
          <table className="table report-table">
            <thead><tr>
              <th>{L("Perusahaan", "Company")}</th>
              <th className="num">{L("Peserta", "Participants")}</th>
              <th className="num">{L("Rata² pretest", "Avg pretest")}</th>
              <th className="num">{L("Rata² posttest", "Avg posttest")}</th>
              <th className="num">{L("Kehadiran", "Attendance")}</th>
              <th className="num">{L("Layak sertifikat", "Eligible")}</th>
            </tr></thead>
            <tbody>
              {perPerusahaan.map((p) => (
                <tr key={p.nama || "tanpa"}>
                  <td className="small" style={{ fontWeight: 500 }}>
                    {p.nama || <span className="muted">{L("Tanpa perusahaan", "No company")}</span>}
                  </td>
                  <td className="mono small tnum num">{p.n}</td>
                  <td className="mono small tnum num">{bulat(p.pretest)}</td>
                  <td className="mono small tnum num">{bulat(p.posttest)}</td>
                  <td className="mono small tnum num">{p.kehadiran == null ? "—" : `${Math.round(p.kehadiran)}%`}</td>
                  <td className="mono small tnum num">{p.layak}/{p.n}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableWrap>
      )}
      <div className="report-note">
        {L("Berkas yang diunduh dari menu “Unduh laporan” berisi: ringkasan nilai dan kehadiran, hasil per peserta dikelompokkan per perusahaan, dan daftar kehadiran per sesi.",
           "The file from the “Download” menu contains: score and attendance summary, per-participant results grouped by company, and per-session attendance.")}
        {daftarPerusahaan.length === 0 && (
          <> {L("Belum ada peserta yang mengisi nama perusahaan, jadi pilihan perusahaan masih kosong.", "No participant has filled in a company name yet, so the company list is empty.")}</>
        )}
      </div>
    </div>
  )
}

/* ── Tab Kepatuhan IMO ────────────────────────────────────────── */
// Pratinjau KELENGKAPAN data, bukan salinan tab Progres: berkas IMO/Hubla
// menuntut tempat-tanggal lahir dan perusahaan tiap peserta, dan kekurangan
// itu lebih baik ketahuan di sini daripada setelah berkas sampai ke regulator.
function ComplianceTab({ L, rows, daftar, memuat, galat, belumDimuat, onRetry, onHapus, bilah }) {
  // Definisi "lengkap" dari server (identitasLengkap: tempat lahir, tanggal
  // lahir, DAN perusahaan) — sama dengan saringan "data identitas belum
  // lengkap", supaya angka ini dan hasil saringannya tidak bertentangan.
  // Cadangan untuk server lama yang belum mengirim bidang itu.
  const lengkap = rows.filter((r) => (typeof r.identitasLengkap === "boolean" ? r.identitasLengkap : r.ttl && r.company)).length
  return (
    <>
      <KpiRow
        items={[
          { label: L("Peserta", "Participants"), value: rows.length },
          {
            label: L("Data identitas lengkap", "Identity data complete"),
            value: `${lengkap}/${rows.length}`,
            sub: L("tempat-tgl lahir & perusahaan terisi", "birth info & company filled"),
          },
          { label: L("Layak sertifikat", "Cert. eligible"), value: `${rows.filter((r) => r.certEligible).length}/${rows.length}` },
        ]}
      />
      <div className="tile">
        <div className="tile-header">
          <h3>{L("Kepatuhan IMO / Hubla", "IMO / Hubla Compliance")}</h3>
          <span className="tile-header-sub">{rows.length}</span>
        </div>
        {rows.length === 0 ? (
          <KosongDenganAksi>{L("Belum ada peserta di cakupan ini.", "No participants in this scope.")}</KosongDenganAksi>
        ) : (<>
          {bilah}
          {galat ? (
            <KosongDenganAksi aksi={<Btn size="sm" onClick={onRetry}>{L("Coba lagi", "Retry")}</Btn>}>
              {L("Hasil saringan gagal dimuat.", "Filtered results failed to load.")}
            </KosongDenganAksi>
          ) : belumDimuat ? (
            <KosongDenganAksi>{L("Memuat…", "Loading…")}</KosongDenganAksi>
          ) : daftar.length === 0 ? (
            <KosongDenganAksi aksi={<Btn size="sm" onClick={onHapus}>{L("Hapus saringan", "Clear filters")}</Btn>}>
              {L("Tidak ada peserta yang cocok dengan pencarian atau saringan ini.", "No participants match this search or filter.")}
            </KosongDenganAksi>
          ) : (
          <div className={memuat ? "report-dimmed" : undefined} aria-busy={memuat}>
          <TableWrap>
            <table className="table report-table">
              <thead><tr>
                <th>{L("Peserta", "Participant")}</th>
                <th>{L("Tempat, Tgl Lahir", "Place, Date of Birth")}</th>
                <th>{L("Perusahaan", "Company")}</th>
                <th className="num">Pretest</th>
                <th className="num">Posttest</th>
                <th>{L("Kehadiran", "Attendance")}</th>
                <th>{L("Sertifikat", "Certificate")}</th>
              </tr></thead>
              <tbody>
                {daftar.map((r) => (
                  <tr key={r.userId}>
                    <td>
                      <div className="small" style={{ fontWeight: 500 }}>{r.name}</div>
                      <div className="tiny muted">{r.email}</div>
                    </td>
                    <td className="small">{r.ttl || <span className="report-missing">{L("Belum diisi", "Missing")}</span>}</td>
                    <td className="small">{r.company || <span className="report-missing">{L("Belum diisi", "Missing")}</span>}</td>
                    <td className="mono small tnum num">{r.pretest != null ? Math.round(r.pretest) : <BelumTes L={L} />}</td>
                    <td className="mono small tnum num">{r.posttest != null ? Math.round(r.posttest) : <BelumTes L={L} />}</td>
                    <td><BarKehadiran nilai={r.attendance} ambang={(r.certThresholds || AMBANG_BAWAAN).attendance} /></td>
                    <td><StatusSertifikat r={r} L={L} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableWrap>
          </div>
          )}
        </>)}
        <div className="report-note">
          {L("Berkas yang diunduh juga memuat sertifikat nautika & Hubla serta nomor sertifikat tiap peserta. Gunakan menu “Unduh laporan” di kanan atas.",
             "The downloaded file also includes each participant's nautical & Hubla certificates and certificate number. Use the “Download” menu at the top right.")}
        </div>
      </div>
    </>
  )
}

/* ── FR-27: Report Schedules tab ─────────────────────────────── */
function ReportSchedulesTab({ pid, lang }) {
  const isMobile = useIsMobile()
  const [schedules, setSchedules] = React.useState([])
  const [loading,   setLoading]   = React.useState(true)
  const [showForm,  setShowForm]  = React.useState(false)
  const [form, setForm]           = React.useState({ report_type: "PARTICIPANTS", frequency: "WEEKLY", email: "" })
  const [saving, setSaving]       = React.useState(false)

  const refresh = React.useCallback(() => {
    if (!pid) return
    setLoading(true)
    listSchedules(pid).then(setSchedules).catch(() => setSchedules([])).finally(() => setLoading(false))
  }, [pid])

  React.useEffect(() => { refresh() }, [refresh])

  async function handleCreate(e) {
    e.preventDefault()
    if (!form.email) { toast.error("Email wajib diisi"); return }
    setSaving(true)
    try {
      await createSchedule(pid, form)
      setShowForm(false)
      setForm({ report_type: "PARTICIPANTS", frequency: "WEEKLY", email: "" })
      refresh()
      toast.success(lang === "id" ? "Jadwal dibuat" : "Schedule created")
    } catch (err) {
      toast.error(err?.response?.data?.error || err.message)
    } finally { setSaving(false) }
  }

  async function handleToggle(s) {
    try {
      await toggleSchedule(pid, s.id, !s.is_active)
      refresh()
    } catch (err) { toast.error(err?.response?.data?.error || err.message) }
  }

  async function handleDelete(s) {
    if (!window.confirm(lang === "id" ? `Hapus jadwal ini?` : `Delete this schedule?`)) return
    try {
      await deleteSchedule(pid, s.id)
      refresh()
      toast.success(lang === "id" ? "Jadwal dihapus" : "Schedule deleted")
    } catch (err) { toast.error(err?.response?.data?.error || err.message) }
  }

  const typeLabel = { PARTICIPANTS: lang === "id" ? "Progres Peserta" : "Participant Progress", COURSES: lang === "id" ? "Progres Kursus" : "Course Progress", ATTENDANCE: lang === "id" ? "Kehadiran" : "Attendance" }
  const freqLabel = { WEEKLY: lang === "id" ? "Mingguan" : "Weekly", MONTHLY: lang === "id" ? "Bulanan" : "Monthly" }

  return (
    <div className="tile">
      <div className="tile-header">
        <h3>{lang === "id" ? "Laporan Terjadwal" : "Scheduled Reports"}</h3>
        <Btn variant="primary" size="sm" icon={<Icon name="plus" size={13} />} onClick={() => setShowForm(true)}>
          {lang === "id" ? "Buat jadwal" : "New schedule"}
        </Btn>
      </div>

      {showForm && (
        <form onSubmit={handleCreate} className="col gap-3" style={{ padding: "16px 20px", borderBottom: "1px solid var(--border)", background: "var(--surface-2)" }}>
          <div className="grid" style={{ gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr 1fr auto", gap: 12, alignItems: "flex-end" }}>
            <div>
              <label className="tiny muted" style={{ display: "block", marginBottom: 4 }}>{lang === "id" ? "Tipe laporan" : "Report type"}</label>
              <select className="search-input" style={{ paddingLeft: 10, width: "100%" }} value={form.report_type} onChange={e => setForm(f => ({ ...f, report_type: e.target.value }))}>
                <option value="PARTICIPANTS">{lang === "id" ? "Progres Peserta" : "Participant Progress"}</option>
                <option value="COURSES">{lang === "id" ? "Progres Kursus" : "Course Progress"}</option>
                <option value="ATTENDANCE">{lang === "id" ? "Kehadiran" : "Attendance"}</option>
              </select>
            </div>
            <div>
              <label className="tiny muted" style={{ display: "block", marginBottom: 4 }}>{lang === "id" ? "Frekuensi" : "Frequency"}</label>
              <select className="search-input" style={{ paddingLeft: 10, width: "100%" }} value={form.frequency} onChange={e => setForm(f => ({ ...f, frequency: e.target.value }))}>
                <option value="WEEKLY">{lang === "id" ? "Mingguan" : "Weekly"}</option>
                <option value="MONTHLY">{lang === "id" ? "Bulanan" : "Monthly"}</option>
              </select>
            </div>
            <div>
              <label className="tiny muted" style={{ display: "block", marginBottom: 4 }}>Email penerima</label>
              <input type="email" required className="search-input" style={{ paddingLeft: 10, width: "100%" }}
                placeholder="admin@example.com" value={form.email}
                onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />
            </div>
            <div className="row gap-2">
              <Btn type="submit" variant="primary" size="sm" disabled={saving}>{lang === "id" ? "Simpan" : "Save"}</Btn>
              <Btn variant="ghost" size="sm" onClick={() => setShowForm(false)}>{lang === "id" ? "Batal" : "Cancel"}</Btn>
            </div>
          </div>
        </form>
      )}

      {loading ? (
        <div className="tile-pad muted small" style={{ textAlign: "center" }}>{lang === "id" ? "Memuat…" : "Loading…"}</div>
      ) : schedules.length === 0 ? (
        <div className="tile-pad" style={{ textAlign: "center", padding: 40 }}>
          <Icon name="calendar" size={28} color="var(--text-4)" />
          <div className="muted small mt-3">
            {lang === "id" ? "Belum ada jadwal. Buat jadwal untuk mengirim laporan CSV otomatis via email." : "No schedules yet. Create one to receive automatic CSV reports by email."}
          </div>
        </div>
      ) : (
        <table className="table">
          <thead><tr>
            <th>{lang === "id" ? "Tipe" : "Type"}</th>
            <th>{lang === "id" ? "Frekuensi" : "Frequency"}</th>
            <th>Email</th>
            <th>{lang === "id" ? "Terakhir terkirim" : "Last sent"}</th>
            <th>{lang === "id" ? "Status" : "Status"}</th>
            <th></th>
          </tr></thead>
          <tbody>
            {schedules.map(s => (
              <tr key={s.id}>
                <td className="small">{typeLabel[s.report_type] || s.report_type}</td>
                <td><span className="tag tiny outline">{freqLabel[s.frequency] || s.frequency}</span></td>
                <td className="mono tiny">{s.email}</td>
                <td className="tiny muted">
                  {s.last_sent_at ? new Date(s.last_sent_at).toLocaleDateString(lang === "id" ? "id-ID" : "en-US") : "—"}
                </td>
                <td>
                  {s.is_active
                    ? <span className="tag tiny green">{lang === "id" ? "Aktif" : "Active"}</span>
                    : <span className="tag tiny outline">{lang === "id" ? "Nonaktif" : "Paused"}</span>}
                </td>
                <td>
                  <div className="row gap-1" style={{ justifyContent: "flex-end" }}>
                    <button className="btn sm ghost icon-only" title={s.is_active ? "Pause" : "Aktifkan"} onClick={() => handleToggle(s)}>
                      <Icon name={s.is_active ? "clock" : "check"} size={13} />
                    </button>
                    <button className="btn sm ghost icon-only" title="Hapus" onClick={() => handleDelete(s)}>
                      <Icon name="trash" size={13} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}
